import { FormRow } from '../../../../components/common/form/Form'
import Label from '../../../../components/common/form/label/Label'
import { t, Trans } from '@lingui/macro'
import Input from '../../../../components/common/form/input/Input'
import {
  Box,
  ButtonClean,
  ButtonDanger,
  ButtonOutline,
  ButtonPrimary,
  Modal
} from '../../../../components'
import React, { FC, useState } from 'react'
import { useGoogleAuth } from '../../../../app/hooks/useGoogleAuth'
import { useModal } from '../../../../app/hooks/useModal'
import { notification } from '../../../../components/common/notifications/Notifications'
import { useSetCalendarIdMutation } from '../AuthenticationTab.codegen'
import { CalendarIntegrationsProps } from './CalendarIntegrations'
import { SyncAlert } from 'front-end/src/pages/manage-events/components/sync-alert/SyncAlert'

export const GoogleCalendarIntegration: FC<
  Pick<CalendarIntegrationsProps, 'calendars' | 'calendarsRefetch'>
> = ({ calendars, calendarsRefetch }) => {
  const [googleCalendarId, setGoogleCalendarId] = useState(
    calendars?.google?.calendarId || ''
  )

  const [setGoogleCalendarIdMutation] = useSetCalendarIdMutation({
    onCompleted: async (data) => {
      if (!data.admin?.organisation?.setGoogleCalendarIdForOrganisation) {
        return
      }
      const result = await calendarsRefetch()
      setGoogleCalendarId(
        result.data?.admin?.organisation?.calendars?.google?.calendarId
      )
      notification.info(t`Calendar integrations updated successfully.`)
    }
  })

  const {
    isAuthenticated: isGoogleAuthenticated,
    authenticate: googleAuthenticate
  } = useGoogleAuth()

  const isSavingDisabled =
    !googleCalendarId || calendars?.google?.calendarId === googleCalendarId

  const showRevokeModal = () => {
    open(DisableGoogleCalendarSyncModal).then(async () => {
      await saveGoogleCalendarId(true)
    })
  }
  const { open, currentModal } = useModal()

  const saveGoogleCalendarId = async (disable = false) => {
    let calendarId = !disable ? googleCalendarId : ''
    await setGoogleCalendarIdMutation({
      variables: {
        calendarId
      }
    })
  }

  return (
    <>
      <FormRow className="AccountPage__calendarInput">
        <Label htmlFor="AccountPage__calendarInput__googleCalendarId">
          <Trans>Calendar ID</Trans>
        </Label>
        <Input
          placeholder="...@group.calendar.google.com"
          id="AccountPage__calendarInput__googleCalendarId"
          value={googleCalendarId}
          onChange={(value) => setGoogleCalendarId(value)}
        />
      </FormRow>

      {calendars?.google?.calendarId && (
        <>
          <ButtonOutline
            mr="12px"
            onClick={async () => {
              if (!isGoogleAuthenticated) {
                const authenticated = await googleAuthenticate()
                if (authenticated) {
                  showRevokeModal()
                }
              } else {
                showRevokeModal()
              }
            }}
          >
            <Trans>Revoke</Trans>
          </ButtonOutline>

          <Box mt="12px" mb="12px">
            <SyncAlert hasDismiss={false} />
          </Box>
        </>
      )}

      <ButtonPrimary
        onClick={async () => {
          if (!isGoogleAuthenticated) {
            const authenticated = await googleAuthenticate()
            if (authenticated) {
              await saveGoogleCalendarId()
            }
          } else {
            await saveGoogleCalendarId()
          }
        }}
        disabled={isSavingDisabled}
      >
        {isGoogleAuthenticated ? (
          <Trans>Save</Trans>
        ) : (
          <Trans>Save and Authenticate</Trans>
        )}
      </ButtonPrimary>
      {currentModal}
    </>
  )
}

const DisableGoogleCalendarSyncModal = ({ resolve, reject }) => (
  <Modal
    newDesign
    header={t`Are you sure you want to disable Google Calendar sync?`}
    controls={
      <>
        <ButtonClean onClick={reject}>
          <Trans>Cancel</Trans>
        </ButtonClean>
        <ButtonDanger onClick={resolve}>
          <Trans>Disable</Trans>
        </ButtonDanger>
      </>
    }
    onHide={reject}
  >
    <p>
      <Trans>
        Given calendar ID will be removed and Google calendar synchronization
        will be disabled.
      </Trans>
    </p>
  </Modal>
)
